import { useRef, useState } from "react";
import { api, formatApiError, IMG } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  BadgeCheck, ShieldQuestion, Clock, XCircle, Upload, CheckCircle2, AlertTriangle,
} from "lucide-react";

const ACCEPTED_IDS = [
  { name: "Aadhaar card",     note: "Front side — full name & DOB visible" },
  { name: "Passport",         note: "First page with photo & personal details" },
  { name: "Voter ID (EPIC)",  note: "Front side — photo, name & EPIC number" },
  { name: "Driving Licence",  note: "Front side — photo, name & DL number" },
  { name: "PAN card",         note: "Full card — name & PAN number visible" },
];

const PHOTO_RULES = [
  "Bright, natural light — no glare or shadow across the ID",
  "All 4 corners of the ID visible in one shot",
  "Text sharp enough to read every letter & number",
  "Original document only — no screenshots or photos of a screen",
  "Document must be currently valid (not expired)",
];

export default function VerificationCard() {
  const { user, refresh } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [previewPath, setPreviewPath] = useState(null);
  const fileRef = useRef(null);

  if (!user) return null;
  // Staff members bypass tenant/landlord verification — they aren't listing
  // properties in this identity, and showing "verify your ID" clutters
  // their portal experience.
  if (["owner", "admin", "sales"].includes(user.role)) return null;
  const status = user.verification_status || "unverified";

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image of your ID");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large — please compress or take a smaller photo (max 8MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreviewPath(data.path);
      await api.post("/verification/submit", { doc_path: data.path });
      toast.success("ID submitted — we'll review it within a few hours");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setUploading(false); }
  };

  // Determine the specific rejection message (long-form, from server)
  const rejectionMessage = user.verification_rejection_reason
    || "The document couldn't be verified. Please re-upload following the guidelines below.";

  const statusMeta = {
    unverified: {
      color: "bg-white border-brand-subtle",
      badge: "text-gray-600 bg-gray-100",
      icon: <ShieldQuestion size={20} className="text-gray-500" />,
      title: "Get the Verified Owner badge",
      body: "Verified owners get a green tick that renters trust — leading to up to 3× more inquiries. Upload a clear photo of any ONE government-issued ID below.",
      cta: "Upload ID document",
    },
    pending: {
      color: "bg-amber-50 border-amber-200",
      badge: "text-amber-700 bg-amber-100",
      icon: <Clock size={20} className="text-amber-600" />,
      title: "Verification in review",
      body: "Thanks! Your ID is with our team — reviews usually complete within a few hours (max 24 hours).",
      cta: null,
    },
    verified: {
      color: "bg-emerald-50 border-emerald-200",
      badge: "text-emerald-700 bg-emerald-100",
      icon: <BadgeCheck size={20} className="text-emerald-600" />,
      title: "You're a Verified Owner",
      body: "Your listings now show a green verified tick — renters see you as a trusted landlord.",
      cta: null,
    },
    rejected: {
      color: "bg-red-50 border-red-200",
      badge: "text-red-700 bg-red-100",
      icon: <XCircle size={20} className="text-red-600" />,
      title: "Verification could not be completed",
      body: rejectionMessage,
      cta: "Re-upload correct ID",
    },
  }[status];

  const showGuidelines = status === "unverified" || status === "rejected";

  return (
    <div className={`rounded-2xl border p-6 md:p-7 ${statusMeta.color}`} data-testid="verification-card">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shrink-0 border border-brand-subtle">
          {statusMeta.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <p className="label-eyebrow text-gray-500">Owner verification</p>
            <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${statusMeta.badge}`}>
              {status}
            </span>
          </div>
          <h3 className="font-display text-2xl brand-forest mb-2">{statusMeta.title}</h3>
          <p className={`text-sm leading-relaxed mb-4 ${status === "rejected" ? "text-red-800" : "text-gray-600"}`}>
            {statusMeta.body}
          </p>

          {showGuidelines && (
            <div className="grid md:grid-cols-2 gap-4 mb-5 bg-white/60 border border-brand-subtle rounded-xl p-4">
              <div>
                <p className="label-eyebrow text-[#7A1E2A] mb-2 flex items-center gap-1.5">
                  <CheckCircle2 size={14} /> Accepted government IDs
                </p>
                <ul className="text-xs text-gray-700 space-y-1.5">
                  {ACCEPTED_IDS.map((id) => (
                    <li key={id.name} className="flex items-start gap-2">
                      <span className="mt-0.5 shrink-0 w-1.5 h-1.5 rounded-full bg-[#C9A24A]" />
                      <span><strong>{id.name}</strong> — <span className="text-gray-500">{id.note}</span></span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="label-eyebrow text-[#7A1E2A] mb-2 flex items-center gap-1.5">
                  <AlertTriangle size={14} /> Photo quality checklist
                </p>
                <ul className="text-xs text-gray-700 space-y-1.5">
                  {PHOTO_RULES.map((rule) => (
                    <li key={rule} className="flex items-start gap-2">
                      <span className="mt-0.5 shrink-0 w-1.5 h-1.5 rounded-full bg-[#7A1E2A]" />
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="md:col-span-2 text-[11px] text-gray-500 mt-1 pt-3 border-t border-brand-subtle">
                🔒 Your ID is stored privately, never shown publicly, and is used only for one-time owner verification.
                We follow Aadhaar / KYC handling norms — you may mask the last 4 digits with a paper strip if you're uploading Aadhaar.
              </p>
            </div>
          )}

          {(status === "unverified" || status === "rejected") && (
            <div className="flex flex-wrap items-center gap-3">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={handleFile}
                className="hidden"
                data-testid="verification-file-input"
              />
              <Button
                data-testid="verification-upload-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="bg-brand-forest hover:bg-[#E64520] text-white rounded-full h-11 px-5"
              >
                <Upload size={16} className="mr-2" />
                {uploading ? "Uploading…" : statusMeta.cta}
              </Button>
              <span className="text-xs text-gray-500">JPG / PNG · max 8MB · kept private</span>
            </div>
          )}

          {previewPath && (
            <img src={IMG(previewPath)} alt="submitted" className="mt-4 w-40 h-24 object-cover rounded-lg border border-brand-subtle" />
          )}
        </div>
      </div>
    </div>
  );
}
